import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Language } from '../models/common/language';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ResultResponse } from '../models/common/result-response';
import { UserLanguage } from '../models/user/user-language';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { MyMessageService } from '../../my-message.service';
import { CldrIntlService, IntlService } from '@progress/kendo-angular-intl';
import { MessageService } from '@progress/kendo-angular-l10n';
import { TranslationLoader } from './translation/translation-loader';
import { NotificationsService } from './notifications.service';

/**
 * Service responsible for managing the language switching functionality.
 */
@Injectable({
  providedIn: 'root',
})
export class LanguageSwitcherService {
  public apiUrl: string = environment.apiUrl;

  public languages: Array<Language> = [
    {
      code: 'usa',
      title: 'English',
      flag: 'https://images.analyzer.global/flags/en.png',
      emoji: '🇺🇸',
      isoCode: 'en-US',
    },
    {
      code: 'd',
      title: 'Deutsch',
      flag: 'https://images.analyzer.global/flags/de.png',
      emoji: '🇩🇪',
      isoCode: 'de-DE',
    },
    {
      code: 'nld',
      title: 'Nederlands',
      flag: 'https://images.analyzer.global/flags/nl.png',
      emoji: '🇳🇱',
      isoCode: 'nl-NL',
    },
  ];

  public selectedLanguage: BehaviorSubject<Language> = new BehaviorSubject(
    this.languages[0],
  );

  constructor(
    private readonly cookieService: CookieService,
    private readonly notificationService: NotificationsService,
    private readonly translate: TranslateService,
    private readonly translationLoader: TranslationLoader,
    private readonly httpClient: HttpClient,
    @Inject(LOCALE_ID) public localeId: string,
    private readonly intlService: IntlService,
    private readonly messages: MessageService,
  ) {
    this.initializeLanguage();
  }
  /**
   * Initializes the language based on the user's preference or the browser language.
   * If a language preference is stored in the cookie, it sets the selected language to that preference.
   * Otherwise, it determines the browser language and sets the selected language accordingly.
   */
  initializeLanguage() {
    const cookie = this.cookieService.get('language');
    if (cookie) {
      const language = this.languages.find((l) => l.isoCode === cookie);

      if (language) {
        this.selectedLanguage.next(language);
        this.translate.use(this.selectedLanguage.getValue().isoCode);
      } else this.getBrowserLanguage();
    } else {
      // Initializing the language based on the browser language
      this.getBrowserLanguage();
    }
  }

  /**
   * Retrieves the language code of the user's browser and sets the active language based on the code.
   */
  getBrowserLanguage() {
    const languageCode = navigator.language.toLowerCase();

    if (languageCode.startsWith('en')) {
      this.setActiveLanguage(this.languages[0]);
    } else if (languageCode.startsWith('de')) {
      this.setActiveLanguage(this.languages[1]);
    } else if (languageCode.startsWith('nl')) {
      this.setActiveLanguage(this.languages[2]);
    } else {
      console.log(
        'Language not found, setting default language: ' +
          this.languages[0].code,
      );
      this.setActiveLanguage(this.languages[0]);
    }
  }

  /**
   * Initializes the active language after a user logs in.
   *
   * @param language - The ISO code of the language to be set as active.
   */
  initializeActiveLanguageAfterLogin(language: string) {
    const languageFound = this.languages.find((l) => l.isoCode === language);

    if (languageFound) {
      this.setActiveLanguage(languageFound, true);
    }
  }

  /**
   * Initializes the locale ID based on the selected language.
   * Sets the locale ID for the CldrIntlService.
   */
  initializeLocaleId() {
    this.localeId = this.selectedLanguage.getValue().isoCode;
    (<CldrIntlService>this.intlService).localeId = this.localeId;
  }

  /**
   * Sets the active language for the language switcher service.
   *
   * @param language - The language to set as active.
   * @param skipSettingToBackend - Optional parameter to skip setting the language to the backend.
   */
  setActiveLanguage(language: Language, skipSettingToBackend?: boolean) {
    this.setLanguageToDocument(language);
    this.setTranslation(language);
    this.setBackendLanguage(language, skipSettingToBackend);
    this.notifyServices(language);
  }
  /**
   * Sets the language for the document.
   *
   * @param language - The language to set.
   */
  setLanguageToDocument(language: Language) {
    this.selectedLanguage.next(language);
    this.cookieService.set('language', language.isoCode);
    document.documentElement.lang = language.isoCode;
    this.localeId = language.isoCode;
  }

  /**
   * Sets the translation for the specified language.
   * @param language The language object containing the ISO code.
   */
  setTranslation(language: Language) {
    this.translationLoader
      .getTranslation(language.isoCode)
      .pipe(take(1))
      .subscribe((translations) => {
        this.translate.setTranslation(language.isoCode, translations, true);
      });

    const languageCodes = ['en-US', 'de-DE', 'nl-NL'];
    const defaultLanguageCode = 'en-US';

    const selectedLanguageCode = languageCodes.includes(language.isoCode)
      ? language.isoCode
      : defaultLanguageCode;

    this.translate.use(selectedLanguageCode);
  }

  /**
   * Sets the backend language for the application.
   *
   * @param language - The language to set as the backend language.
   * @param skipSettingToBackend - Optional parameter to skip setting the language to the backend.
   */
  setBackendLanguage(language: Language, skipSettingToBackend?: boolean) {
    if (!skipSettingToBackend) {
      this.setBackEndLanguage(language.isoCode)
        .pipe(take(1))
        .subscribe((result) => {
          if (result.Success) {
            this.notificationService.showLanguageChanged();
          }
        });
    }
  }

  /**
   * Notifies the services about the language change.
   * @param language The new language to be set.
   */
  notifyServices(language: Language) {
    (<CldrIntlService>this.intlService).localeId = this.localeId;
    (<CldrIntlService>this.intlService).notify();

    const svc: MyMessageService = this.messages as MyMessageService;
    svc.language = language.code;
    svc.notify();
  }

  /**
   * Sets the backend language for the user.
   * @param languageCode - The language code to set.
   */
  setBackEndLanguage(languageCode: string): Observable<ResultResponse> {
    const url = this.apiUrl + `api/v2/User/SetUserLanguage`;

    const body: UserLanguage = {
      Language: languageCode,
    };

    return this.httpClient.patch<ResultResponse>(url, body);
  }
}
